import React from 'react';
import { CloudOff, RefreshCw, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';

/**
 * Settings section mirroring the offline sync queue. Reuses the single
 * queue instance owned by LibraryPage (passed via props) — never spins
 * up its own useOfflineQueue, so there's exactly one flush loop.
 * Renders nothing when there's nothing pending.
 */
export const PendingSyncSettings = ({ count = 0, flushing = false, onFlush, onOpen }) => {
  const { t } = useLanguage();
  if (count === 0) return null;

  const label = count === 1
    ? (t('pendingSyncPillOne') || '1 change pending sync')
    : (t('pendingSyncPill') || '{n} changes pending sync').replace('{n}', String(count));

  return (
    <div className="space-y-3 border-t pt-4" data-testid="settings-pending-sync-section">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-base font-medium" style={{ color: 'hsl(35, 60%, 28%)' }}>
            <CloudOff className="w-4 h-4" aria-hidden="true" />
            {t('pendingSyncTitle') || 'Changes waiting to sync'}
          </div>
          <p className="text-xs mt-0.5" style={{ color: 'hsl(125, 12%, 40%)' }}>{label}</p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onOpen}
            className="gap-1"
            data-testid="settings-pending-sync-view"
          >
            {t('pendingSyncViewDetails') || 'Details'}
            <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
          </Button>
          <Button
            size="sm"
            onClick={onFlush}
            disabled={flushing}
            className="gap-1.5"
            style={{ backgroundColor: 'hsl(143, 28%, 40%)', color: 'white' }}
            data-testid="settings-pending-sync-retry"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${flushing ? 'animate-spin' : ''}`} aria-hidden="true" />
            {flushing ? (t('pendingSyncSyncing') || 'Syncing…') : (t('pendingSyncRetry') || 'Retry now')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PendingSyncSettings;
